import { useEffect, useRef, useState } from 'react'
import { LogOut, User } from 'lucide-react'

import GraphUserAvatar from '@/shared/components/ui/UserAvatar'

interface UserMenuProps {
  name: string
  email: string
  onProfile: () => void
  onLogout: () => void
}

const ITEM_CLASS =
  'w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-50 hover:text-primary transition-colors'

/** Avatar trigger with a Profile / Log out dropdown. Navigation is the caller's job. */
export default function UserMenu({ name, email, onProfile, onLogout }: UserMenuProps) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return

    function handleMouseDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('mousedown', handleMouseDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex rounded-full ring-offset-1 transition-shadow hover:ring-2 hover:ring-gray-200 focus:outline-none focus:ring-2 focus:ring-primary"
      >
        <GraphUserAvatar email={email} displayName={name || email} size={32} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
        >
          <div className="flex items-center gap-2 px-3 py-3 min-w-0">
            <GraphUserAvatar email={email} displayName={name || email} size={40} />
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-800 truncate">{name || email}</p>
              <p className="text-xs text-gray-500 truncate">{email}</p>
            </div>
          </div>

          <div className="border-t border-gray-200" />

          <button
            type="button"
            role="menuitem"
            className={ITEM_CLASS}
            onClick={() => {
              setOpen(false)
              onProfile()
            }}
          >
            <User size={16} />
            Profile
          </button>

          <div className="border-t border-gray-200" />

          <button
            type="button"
            role="menuitem"
            className={ITEM_CLASS}
            onClick={() => {
              setOpen(false)
              onLogout()
            }}
          >
            <LogOut size={16} />
            Log out
          </button>
        </div>
      )}
    </div>
  )
}
